import { cn } from "@/lib/utils";
import { useRootStore } from "@/state/RootStore";
import { TestMode } from "@/state/TestsStore";
import {
  Maximize2,
  ShieldAlert,
  Lightbulb,
  FileText,
  List,
  Menu,
  ChevronLeft,
  ChevronRight,
  Flag,
} from "lucide-react";
import posthog, { captureEvent } from "@/utils/posthog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useUser } from "@clerk/clerk-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { useToggle } from "react-use";
import { QuestionReportModal } from "./QuestionReportModal";
import ExamCountdown from "./ExamCountdown";
import { getExamTimeRemainingMs, isExpired } from "@/utils/examTimer";
import { ParsedHintText } from "./ParsedHint";
import { APP_TO_BUILD, PRACTICE_TYPE_DATA, PracticeTestType } from "../../../appsConfig";
import { getExamDisplayNames } from "@/utils/examSelection";
import SubmitExamModal from "./SubmitExamModal";
import ExitExamModal from "./ExitExamModal";
import {
  ExamAnswerOption,
  type ExamAnswerOptionState,
} from "./ExamAnswerOption";
import { ExamExhibit } from "./ExamExhibit";
import wrongFilterIcon from "@/assets/images/wrong_filter_icon.png";
import { observer } from "mobx-react";
import {
  isPassageParent,
  isPassageChild,
} from "@petergok/blue-collar-shared";
import { useTranslation } from "react-i18next";
import { QuestionRailStatus } from "./QuestionRailStatus";

function getPassageBody(text: string | undefined, title: string): string | undefined {
  if (!text) return text;
  const [firstLine, ...remainingLines] = text.split("\n");
  if (firstLine.trim() !== title) return text;
  return remainingLines.join("\n").trimStart();
}

const ExamScreen = observer(() => {
  const store = useRootStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: clerkUser } = useUser();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const test = store.testStore.activeTest;

  const requestedReviewResultId = location.state?.reviewResultId;
  const reviewResultId =
    typeof requestedReviewResultId === "string" &&
    requestedReviewResultId === test?.id
      ? requestedReviewResultId
      : test?.isFinished
        ? test.id
        : undefined;
  const reviewResultOrigin =
    location.state?.resultOrigin === "past-exams" ? "past-exams" : undefined;

  const selectedStateCode = store.mainAppStore.selectedStateCode;

  // Test navigation
  const requestedReviewQuestionIndex = Number(location.state?.reviewQuestionIndex);
  const initialQuestionIndex = Number.isInteger(requestedReviewQuestionIndex) &&
    requestedReviewQuestionIndex >= 0 &&
    requestedReviewQuestionIndex < (test?.questions?.length ?? 0)
    ? requestedReviewQuestionIndex
    : 0;
  const [questionIndex, setQuestionIndex] = useState<number>(initialQuestionIndex);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [questionsDrawerOpen, setQuestionsDrawerOpen] = useState(false);
  const questionRailRef = useRef<HTMLElement>(null);
  const [seenQuestionIndices, setSeenQuestionIndices] = useState<Set<number>>(
    new Set()
  );
  const questionId = test?.questions?.[questionIndex];
  const selectedAnswer = test?.selectedAnswers?.[questionIndex];

  const isQuestionSubmitted = test?.submittedQuestions?.[questionIndex];

  // Read the localized question (resolves *_i18n fields and falls back to
  // English / plain field if missing). Reactive on language change via the
  // useTranslation() i18n.language above.
  const rawQuestion = useMemo(
    () =>
      store.questionsStore.questions.get(questionId || "") ||
      store.questionsStore.deletedQuestions.get(questionId || ""),
    [
      questionId,
      store.questionsStore.questions,
      store.questionsStore.deletedQuestions,
    ],
  );
  const question = useMemo(
    () => store.questionsStore.getLocalizedQuestion(questionId || ""),
    [questionId, currentLanguage, rawQuestion, store.questionsStore],
  );

  // Precompute localized questions for the sidebar map; without this, every
  // render of ExamScreen recomputes 100+ localized objects, allocates new
  // identities, and would defeat any downstream React.memo. Keyed on
  // currentLanguage so a switch invalidates the whole map.
  const localizedQuestionsByIndex = useMemo(() => {
    const map = new Map<number, ReturnType<typeof store.questionsStore.getLocalizedQuestion>>();
    test?.questions?.forEach((qId, idx) => {
      map.set(idx, store.questionsStore.getLocalizedQuestion(qId));
    });
    return map;
  }, [
    test?.questions,
    currentLanguage,
    store.questionsStore,
    store.questionsStore.questions,
    store.questionsStore.deletedQuestions,
  ]);

  // Passage detection
  // Cast: isPassageParent/Child require the strict Question shape; web reads
  // questions as untyped Supabase rows, and our QuestionWithI18n keeps fields
  // optional to accommodate that shape mismatch.
  const currentIsPassageParent = question
    ? isPassageParent(question as any)
    : false;
  const currentIsPassageChild = question
    ? isPassageChild(question as any)
    : false;

  // Get passage info for the current question
  const passageInfo = useMemo(() => {
    if (!question) return null;

    // Helper to extract title from passage text (first line)
    const extractTitle = (text: string | undefined): string => {
      if (!text) return t("test.passage_default_title");
      return text.split('\n')[0].trim();
    };

    // If this is a passage parent, return the passage text
    if (currentIsPassageParent) {
      const passageTitle = extractTitle(question.question);
      return {
        isPassageParent: true,
        passageText: getPassageBody(question.question, passageTitle),
        passageTitle,
        parentQuestionId: question.question_id,
      };
    }

    // If this is a passage child, find the parent and return its passage text.
    // Use getLocalizedQuestion so the passage text/title resolve to the active
    // language (the raw maps hold English-only fields).
    if (currentIsPassageChild && question.parent_question_id) {
      const parentQuestion = store.questionsStore.getLocalizedQuestion(
        question.parent_question_id,
      );

      if (parentQuestion) {
        const passageTitle = extractTitle(parentQuestion.question);
        return {
          isPassageParent: false,
          passageText: getPassageBody(parentQuestion.question, passageTitle),
          passageTitle,
          parentQuestionId: question.parent_question_id,
        };
      }
    }

    return null;
  }, [question, currentIsPassageParent, currentIsPassageChild, currentLanguage, store.questionsStore, t]);

  // Build passage groupings for the sidebar navigation
  const passageGroups = useMemo(() => {
    if (!test?.questions) return { groups: [], questionToGroup: new Map<number, { passageNumber: number; relativeIndex: number }>() };

    const groups: { passageNumber: number; parentIndex: number; childIndices: number[] }[] = [];
    const questionToGroup = new Map<number, { passageNumber: number; relativeIndex: number }>();
    let passageCount = 0;

    test.questions.forEach((qId, index) => {
      const q = store.questionsStore.questions.get(qId) || store.questionsStore.deletedQuestions.get(qId);
      if (!q) return;

      if (isPassageParent(q)) {
        passageCount++;
        groups.push({ passageNumber: passageCount, parentIndex: index, childIndices: [] });
        questionToGroup.set(index, { passageNumber: passageCount, relativeIndex: 0 });
      } else if (isPassageChild(q)) {
        // Find the group this child belongs to
        const parentGroup = groups.find((g) => {
          const parentQ = store.questionsStore.questions.get(test.questions[g.parentIndex]) ||
            store.questionsStore.deletedQuestions.get(test.questions[g.parentIndex]);
          return parentQ?.question_id === q.parent_question_id;
        });
        if (parentGroup) {
          parentGroup.childIndices.push(index);
          questionToGroup.set(index, {
            passageNumber: parentGroup.passageNumber,
            relativeIndex: parentGroup.childIndices.length,
          });
        }
      }
    });

    return { groups, questionToGroup };
  }, [test?.questions, store.questionsStore.questions, store.questionsStore.deletedQuestions]);

  // Calculate the display question number (excludes passage parents from count)
  const displayQuestionNumber = useMemo(() => {
    const passageParentsBefore = passageGroups.groups.filter(g => g.parentIndex < questionIndex).length;
    return questionIndex + 1 - passageParentsBefore;
  }, [passageGroups.groups, questionIndex]);
  const totalDisplayQuestions = Math.max(
    0,
    (test?.questions?.length ?? 0) - passageGroups.groups.length,
  );
  const currentPassageNumber =
    passageGroups.questionToGroup.get(questionIndex)?.passageNumber ?? 1;
  const examProgress = totalDisplayQuestions > 0
    ? Math.min(100, (displayQuestionNumber / totalDisplayQuestions) * 100)
    : 0;
  const testPresentation = test
    ? PRACTICE_TYPE_DATA[test.practiceTestType as keyof typeof PRACTICE_TYPE_DATA]
    : undefined;

  const [showAllToggleIndex, setShowAllToggleIndex] = useState<number>(0);

  // Close the mobile Questions drawer whenever the active question changes.
  useEffect(() => {
    setQuestionsDrawerOpen(false);
  }, [questionIndex]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      questionRailRef.current
        ?.querySelector<HTMLElement>('[aria-current="true"]')
        ?.scrollIntoView({ block: "nearest" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [questionIndex, railCollapsed, showAllToggleIndex]);

  // Close the Questions drawer on Escape while it is open.
  useEffect(() => {
    if (!questionsDrawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setQuestionsDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [questionsDrawerOpen]);

  // Hint visibility — declared early so the question-tracking effect below
  // can reset it on question change without a TDZ violation.
  const [showHint, toggleShowHint] = useToggle(false);

  // Track questions used when seen
  useEffect(() => {
    // Don't track in review mode
    if (test?.isFinished) {
      return;
    }

    if (!seenQuestionIndices.has(questionIndex)) {
      setSeenQuestionIndices(new Set([...seenQuestionIndices, questionIndex]));

      store.questionsStore.addUsedQuestions([questionId || ""]);
    }

    toggleShowHint(false);
  }, [
    questionIndex,
    questionId,
    seenQuestionIndices,
    test?.isFinished,
    store.questionsStore,
    toggleShowHint,
  ]);


  useEffect(() => {
    const handleCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      const year = new Date().getFullYear();
      e.clipboardData?.setData(
        "text/plain",
        t("test.copy_restricted", { year })
      );
      alert(t("test.copy_restricted_alert", { year }));
    };

    document.addEventListener("copy", handleCopy);

    // Cleanup on unmount
    return () => {
      document.removeEventListener("copy", handleCopy);
    };
  }, [t]);

  const [showSubmitModal, toggleShowSubmitModal] = useToggle(false);
  const [showExitModal, toggleShowExitModal] = useToggle(false);

  // One-shot latch shared by BOTH completion paths (timer expiry and the Submit button).
  // Submitting twice would fire a second analytics event and a second navigation, and
  // the timer interval keeps firing until cleanup.
  //
  // Also gates the no-test fallback below: `submitTest()` clears `activeTest`, so
  // without this the fallback would see `!test` and bounce the student to /study in the
  // same tick we navigate them to their results.
  const finalizingRef = useRef(false);

  const examTimeMinutes =
    store.questionsStore.getTotalExamTimeMinutes(selectedStateCode);

  // Only ever RE-ARMS, never disarms: reset is conditional on there being a test id, so
  // the transition to `undefined` that finalizing causes cannot clear the latch. Under a
  // persistent layout or keep-alive route exam #2 would otherwise inherit `true` and
  // silently never auto-submit.
  useEffect(() => {
    if (test?.id) finalizingRef.current = false;
  }, [test?.id]);

  /**
   * The single way an exam completes. Both the timer and the Submit button route through
   * here so they cannot drift: one saved test, one analytics event, one navigation.
   */
  const finalizeExam = useCallback(
    (reason: "manual" | "timer") => {
      if (finalizingRef.current) return;
      finalizingRef.current = true;

      // Captured BEFORE submitting: submitTest() clears `activeTest`, and the results
      // route needs the id.
      const finishedTestId = test?.id;
      const testMode = test?.testMode;

      test?.submitTest();

      captureEvent("practice_mode_submit", {
        practice_mode_type: PracticeTestType.enum[testMode as TestMode],
        ...(reason === "timer" ? { auto_submitted: true } : {}),
      });

      // `replace`, so the back button cannot return to a submitted exam. Falling back to
      // /study rather than `/exam-result/undefined` if the id was somehow lost.
      navigate(finishedTestId ? `/exam-result/${finishedTestId}` : "/study", {
        replace: true,
      });
    },
    [test, navigate],
  );

  // Timer: only check time-limit for EXAM mode; no setState so we don't trigger
  // a full ExamScreen re-render every tick (re-renders cascade into
  // localizedQuestionsByIndex recomputation and were causing 48–69 second INP
  // freezes for long sessions).
  useEffect(() => {
    if (test?.testMode !== TestMode.EXAM || test?.isFinished) return;
    const id = setInterval(() => {
      // SAME definition of "expired" the countdown uses. Previously this had its own
      // `moment().diff(...) >= examTimeMinutes` formulation: numerically equivalent
      // today, but two definitions drift the moment one is edited — and this one
      // silently fails on an invalid startTime, where `NaN >= n` is false and the exam
      // would never end. `isExpired(null)` is false, so an unreadable clock does not
      // auto-submit a student's exam either.
      const remainingMs = getExamTimeRemainingMs(
        test?.startTime?.valueOf() ?? NaN,
        examTimeMinutes,
        Date.now(),
      );
      if (isExpired(remainingMs)) {
        // Auto-submit used to end here without navigating, leaving the student on a
        // blank frame: no "time's up", no score, no way forward but hunting for Exit.
        // It also called `submitTest(navigation)` — an undeclared identifier resolving
        // to the Chromium-only Navigation API global, which threw a ReferenceError on
        // Safari and Firefox. Both are gone now that this shares finalizeExam with the
        // manual path.
        finalizeExam("timer");
      }
    }, 1000);
    return () => clearInterval(id);
  }, [test, examTimeMinutes, finalizeExam]);

  // Pause PostHog session_recording (rrweb) while taking an exam. rrweb DOM
  // snapshotting + the answer flow's localStorage writes + 500ms timer churn
  // combined to block the main thread for tens of seconds. Recording resumes
  // on unmount so the rest of the app is still captured.
  useEffect(() => {
    posthog?.stopSessionRecording?.();
    return () => {
      posthog?.startSessionRecording?.();
    };
  }, []);

  // Report incorrect question state
  const [showReportIncorrectQn, toggleShowReportIncorrectQn] = useToggle(false);

  const handleAnswerClick = useCallback((answerIndex: number) => {
    if (isQuestionSubmitted || test?.isFinished) {
      return;
    }

    test?.selectAnswer(questionIndex, answerIndex);
    captureEvent("question_answer", {
      answer_selected: question?.["answer_" + answerIndex],
      question: question?.question,
      questionId: questionId || "",
    });
  }, [isQuestionSubmitted, test, questionIndex, question, questionId]);

  const renderAnswers = () => {
    return Array.from(new Array(7).keys()).map((answerIndex) => {
      const answer = question?.["answer_" + answerIndex];
      if (answer) {
        const showAnswer = isQuestionSubmitted || test?.isFinished;
        const correctAnswer =
          showAnswer &&
          store.questionsStore.isAnswerCorrect(question, answerIndex);
        const wrongAnswer =
          showAnswer && answerIndex == selectedAnswer && !correctAnswer;

        let state: ExamAnswerOptionState = "default";
        if (correctAnswer) {
          state = "correct";
        } else if (wrongAnswer) {
          state = "incorrect";
        } else if (selectedAnswer === answerIndex && !showAnswer) {
          state = "selected";
        }

        return (
          <ExamAnswerOption
            answer={answer}
            key={answerIndex}
            onSelect={() => handleAnswerClick(answerIndex)}
            state={state}
            statusText={
              correctAnswer
                ? t("test.answer_correct_sr")
                : wrongAnswer
                  ? t("test.answer_incorrect_sr")
                  : undefined
            }
          />
        );
      }
    });
  };

  /**
   * Returns the next index of an incorrectly answered question, or null if none
   */
  const getNextIncorrectAnswerIndex = () => {
    let index = questionIndex + 1;
    while (index < (test?.questions?.length ?? 0)) {
      const lookupQuestionId = test?.questions?.[index];
      const lookupQuestion =
        store.questionsStore.questions.get(lookupQuestionId || "") ||
        store.questionsStore.deletedQuestions.get(lookupQuestionId || "");

      if (!lookupQuestion) {
        index += 1;
        continue;
      }

      const selectedAnswerIndex = test?.selectedAnswers?.[index];
      const answerCorrect =
        selectedAnswerIndex != null &&
        store.questionsStore.isAnswerCorrect(
          lookupQuestion,
          selectedAnswerIndex,
        );

      if (!Boolean(answerCorrect)) {
        return index;
      }

      index += 1;
    }

    return null;
  };

  /**
   * Returns the previous index of an incorrectly answered question, or null if none
   */
  const getPreviousIncorrectAnswerIndex = () => {
    let index = questionIndex - 1;
    while (index >= 0) {
      const lookupQuestionId = test?.questions?.[index];
      const lookupQuestion =
        store.questionsStore.questions.get(lookupQuestionId || "") ||
        store.questionsStore.deletedQuestions.get(lookupQuestionId || "");

      if (!lookupQuestion) {
        index -= 1;
        continue;
      }

      const selectedAnswerIndex = test?.selectedAnswers?.[index];
      const answerCorrect =
        selectedAnswerIndex != null &&
        store.questionsStore.isAnswerCorrect(
          lookupQuestion,
          selectedAnswerIndex,
        );

      if (!Boolean(answerCorrect)) {
        return index;
      }

      index -= 1;
    }

    return null;
  };

  const nextQuestion = () => {
    // If only filtering for incorrect questions, make sure to skip to the next incorrect one
    if (showAllToggleIndex === 1) {
      const nextIndex = getNextIncorrectAnswerIndex();

      if (nextIndex !== null) {
        setQuestionIndex(nextIndex);
      }
      return;
    }

    if (questionIndex >= (test?.questions?.length ?? 0) - 1) {
      return;
    }

    setQuestionIndex(questionIndex + 1);
  };

  const previousQuestion = () => {
    // If only filtering for incorrect questions, make sure to skip to the previous incorrect one
    if (showAllToggleIndex === 1) {
      const prevIndex = getPreviousIncorrectAnswerIndex();

      if (prevIndex !== null) {
        setQuestionIndex(prevIndex);
      }
      return;
    }

    if (questionIndex === 0) {
      return;
    }

    setQuestionIndex(questionIndex - 1);
  };

  const submitAnswer = () => {
    test?.submitAnswer(questionIndex);
  };

  const diagramUrl = question?.diagram
    ? String(question.diagram).replace(
        "https://storage.cloud.google.com",
        "https://storage.googleapis.com"
      )
    : undefined;

  const canSeeSubmit =
    !test?.isFinished && !isQuestionSubmitted && test?.canSeeAnswer;
  const isLastQuestion =
    questionIndex === (test?.questions.length ?? 0) - 1;
  const nextButtonIsPrimary = Boolean(
    test?.canSeeAnswer && isQuestionSubmitted,
  );
  let previousDisabled = questionIndex === 0;
  let nextDisabled = questionIndex === test?.questions.length - 1;

  // When filtering for incorrect only, make sure to check if there is a previous incorrect one available to see
  if (showAllToggleIndex === 1) {
    previousDisabled = getPreviousIncorrectAnswerIndex() === null;
  }

  // When filtering for incorrect only, make sure to check if there is a next incorrect one available to see
  if (showAllToggleIndex === 1) {
    nextDisabled = getNextIncorrectAnswerIndex() === null;
  }

  const userEmail = store.mainAppStore.profileEmail || clerkUser?.emailAddresses[0]?.emailAddress;

  const handleShowExitModal = useCallback(() => {
    if (test?.isFinished) {
      // In case the user starred anything in review
      store.testStore.saveTestsToDevice();

      if (reviewResultId) {
        navigate(`/exam-result/${encodeURIComponent(reviewResultId)}`, {
          replace: true,
          state: reviewResultOrigin
            ? { resultOrigin: reviewResultOrigin }
            : undefined,
        });
        return;
      }

      navigate(store.mainAppStore.hasStudyAccess(true) ? "/study" : "/paywall");
    } else {
      toggleShowExitModal(true);
    }
  }, [
    navigate,
    reviewResultId,
    reviewResultOrigin,
    store.mainAppStore,
    store.testStore,
    test,
    toggleShowExitModal,
  ]);

  const handleExitExam = useCallback(() => {
    store.testStore.discardTest(test?.id || "");
    store.testStore.saveTestsToDevice();
    captureEvent("practice_mode_quit");
    navigate("/study");
  }, [test, store.testStore, navigate]);

  const handleSubmitExam = useCallback(() => {
    finalizeExam("manual");
  }, [finalizeExam]);

  // Reactive, not mount-only. The previous form had an empty dep array, so a test that
  // disappeared after mount (discarded in another tab, cleared by an identity change)
  // left the student on a dead exam screen forever.
  //
  // `finalizingRef` is what makes this safe to run reactively: submitting clears
  // `activeTest`, and without that guard this would race the results navigation and
  // send the student to /study instead of their score.
  useEffect(() => {
    if (!test && !finalizingRef.current) navigate("/study", { replace: true });
  }, [test, navigate]);

  const handleSelectQuestion = useCallback((qIndex: number) => {
    setQuestionIndex(qIndex);
    setQuestionsDrawerOpen(false);
  }, []);

  const handleStarQuestion = useCallback((qIndex: number, questionId: string, currentlyStarred: boolean) => {
    test?.setQuestionStarred(qIndex, !currentlyStarred);
    captureEvent("question_starred", {
      question: qIndex,
      questionId: questionId || "",
      is_starred: !currentlyStarred,
    });
  }, [test]);

  const necYear = useMemo(() => {
    return selectedStateCode ?
        store.questionsStore.getEffectiveCodebookYear(selectedStateCode) :
        null;
  }, [selectedStateCode, store.questionsStore]);
  const examSubtitle = useMemo(() => {
    if (APP_TO_BUILD === "ibew_aptitude") return t("study.ibew_header_subtitle");
    const stateName = selectedStateCode
      ? store.questionsStore.stateInfo?.stateNames?.[selectedStateCode] || ""
      : "";
    const examName = selectedStateCode
      ? store.questionsStore.stateInfo?.stateExamNames?.[selectedStateCode] || stateName
      : stateName;
    if (APP_TO_BUILD !== "electrical_us") return examName;
    const { examLabel } = getExamDisplayNames(examName, stateName);
    return `${examLabel || t("study.exam_fallback")} - NEC ${necYear || ""}`.trim();
  }, [necYear, selectedStateCode, store.questionsStore.stateInfo, t]);

  const hintContent = useMemo((): string | null => {
    if (!question) return null;
    const codeRef = necYear != null ? question?.codes?.[String(necYear)] : null;
    const walkthrough = question?.walkthrough ?? '';
    if (!codeRef && !walkthrough) return question?.hint ?? null;
    return [codeRef, walkthrough].filter(Boolean).join('\n\n');
  }, [question, necYear]);

  return (
    <div className="flex h-dvh min-h-0 w-full flex-col overflow-hidden bg-[#f4f4f6]">
      {/* Main content area with sidebar and question */}
      <div className="flex min-h-0 flex-1 items-stretch">
        {/* Backdrop for the mobile Questions drawer */}
        {questionsDrawerOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            onClick={() => setQuestionsDrawerOpen(false)}
          />
        )}
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-50 flex h-full w-[240px] shrink-0 flex-col overflow-hidden border-r border-[#dddfe4] bg-white transition-transform duration-200 lg:relative lg:z-20 lg:translate-x-0 lg:transition-[width]",
            questionsDrawerOpen ? "translate-x-0" : "-translate-x-full",
            railCollapsed ? "lg:w-[76px]" : "lg:w-[240px]",
          )}
        >
          <header className={cn("flex h-20 w-full shrink-0 items-center overflow-hidden border-b border-[#dddfe4] px-6", railCollapsed ? "justify-center" : "justify-between")}>
            <div className="flex min-w-0 items-center gap-3">
              {!railCollapsed && (
                <div className="flex items-center gap-3 text-lg font-semibold leading-[27px] text-[#0a0c11]">
                  <List aria-hidden="true" className="h-6 w-6 text-[var(--dp-brand)]" />
                  {t("test.questions_sidebar_title")}
                </div>
              )}
              {showAllToggleIndex === 1 && (
                <img alt="" src={wrongFilterIcon} className="size-6" />
              )}
            </div>
            <button
              aria-label={railCollapsed ? t("results.expand_questions") : t("results.collapse_questions")}
              className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#dddfe4] text-[#5b616d] hover:bg-[#f4f4f6] lg:flex"
              onClick={() => setRailCollapsed((collapsed) => !collapsed)}
              type="button"
            >
              {railCollapsed ? <ChevronRight aria-hidden="true" className="h-4 w-4" /> : <ChevronLeft aria-hidden="true" className="h-4 w-4" />}
            </button>
          </header>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {test?.isFinished && !railCollapsed && (
              <div
                aria-label={t("test.questions_sidebar_title")}
                className="mx-4 my-4 flex h-12 shrink-0 rounded-full bg-[#f4f4f6] p-1"
                role="tablist"
              >
                <button
                  aria-selected={showAllToggleIndex === 0}
                  onClick={() => setShowAllToggleIndex(0)}
                  className={cn(
                    "flex-1 rounded-full px-3 text-sm font-medium transition-[background-color,color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand)] focus-visible:ring-offset-1",
                    showAllToggleIndex === 0
                      ? "bg-white text-black shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  )}
                  role="tab"
                  type="button"
                >
                  {t("test.tab_all")}
                </button>
                <button
                  aria-selected={showAllToggleIndex === 1}
                  onClick={() => setShowAllToggleIndex(1)}
                  className={cn(
                    "flex-1 rounded-full px-3 text-sm font-medium transition-[background-color,color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand)] focus-visible:ring-offset-1",
                    showAllToggleIndex === 1
                      ? "bg-white text-black shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  )}
                  role="tab"
                  type="button"
                >
                  {t("test.tab_incorrect")}
                </button>
              </div>
            )}

            <nav
              aria-label={t("test.questions_sidebar_title")}
              className={cn(
                "min-h-0 flex-1 overflow-y-auto",
                railCollapsed && "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
              )}
              ref={questionRailRef}
            >
              {test?.questions?.map((innerQuestionId, qIndex) => {
                // Localized so the rendered question/answers match the
                // current language; falls back to English when *_i18n
                // fields are absent. Read from the precomputed map so we
                // don't recompute 100+ localized objects on every render.
                const lookupQuestion = localizedQuestionsByIndex.get(qIndex);

                const showAnswer =
                  test?.submittedQuestions[qIndex] || test?.isFinished;
                const selectedAnswerIndex =
                  test?.selectedAnswers[qIndex];
                const answerCorrect =
                  showAnswer &&
                  selectedAnswerIndex != null &&
                  store.questionsStore.isAnswerCorrect(
                    lookupQuestion,
                    selectedAnswerIndex,
                  );

                if (showAllToggleIndex === 1 && answerCorrect) {
                  return null;
                }

                if (!lookupQuestion) {
                  return null;
                }

                // Check if this is a passage parent
                const isParent = isPassageParent(lookupQuestion as any);
                const passageGroupInfo = passageGroups.questionToGroup.get(qIndex);

                // Calculate question number excluding passage parents
                const passageParentsBefore = passageGroups.groups.filter(g => g.parentIndex < qIndex).length;
                const questionNumber = qIndex + 1 - passageParentsBefore;

                if (isParent && passageGroupInfo) {
                  const active = qIndex === questionIndex;
                  return (
                    <button
                      key={qIndex}
                      type="button"
                      onClick={() => handleSelectQuestion(qIndex)}
                      aria-current={active ? "true" : undefined}
                      aria-label={t("test.passage_label", { number: passageGroupInfo.passageNumber })}
                      className={cn(
                        "relative flex h-14 w-full shrink-0 items-center overflow-hidden border-b border-[#dddfe4] px-7 py-4 text-left text-base font-medium leading-6 text-[#5b616d] hover:bg-[#f4f4f6]",
                        railCollapsed && "justify-center gap-1 px-2",
                        active && "bg-[var(--dp-brand-soft)] text-[var(--dp-brand)] hover:bg-[var(--dp-brand-soft)]",
                      )}
                    >
                      {active && <span aria-hidden="true" className="absolute left-0 top-4 h-6 w-[3px] rounded-r-full bg-[var(--dp-brand)]" />}
                      <span className="flex items-center gap-2 truncate">
                        <FileText aria-hidden="true" className="h-5 w-5 shrink-0" />
                        {!railCollapsed && t("test.passage_label", { number: passageGroupInfo.passageNumber })}
                      </span>
                    </button>
                  );
                }

                const active = qIndex === questionIndex;
                const flagged = Boolean(test?.starredQuestions[qIndex]);
                const status = showAnswer
                  ? t(answerCorrect ? "results.correct" : "results.incorrect")
                  : null;
                const questionAriaLabel = status
                  ? t(
                      flagged
                        ? "results.question_result_flagged_aria"
                        : "results.question_result_aria",
                      { number: questionNumber, status },
                    )
                  : flagged
                    ? t("test.go_to_flagged_question_aria", { number: questionNumber })
                    : t("test.go_to_question_aria", {
                        number: questionNumber,
                        defaultValue: "Go to question {{number}}",
                      });
                return (
                  <button
                    key={qIndex}
                    aria-current={active ? "true" : undefined}
                    aria-label={questionAriaLabel}
                    className={cn(
                      "relative flex h-14 w-full shrink-0 items-center overflow-hidden border-b border-[#dddfe4] px-7 py-4 text-left text-base font-medium leading-6 text-[#5b616d] hover:bg-[#f4f4f6]",
                      railCollapsed && "justify-center gap-1 px-2",
                      active && "bg-[var(--dp-brand-soft)] text-[var(--dp-brand)] hover:bg-[var(--dp-brand-soft)]",
                    )}
                    onClick={() => handleSelectQuestion(qIndex)}
                    type="button"
                  >
                    {active && <span aria-hidden="true" className="absolute left-0 top-4 h-6 w-[3px] rounded-r-full bg-[var(--dp-brand)]" />}
                    <span className={cn("flex-1", railCollapsed && "flex-none")}>#{questionNumber}</span>
                    <QuestionRailStatus
                      correct={answerCorrect}
                      flagged={flagged}
                      showAnswer={Boolean(showAnswer)}
                    />
                  </button>
                );
              })}
            </nav>
          </div>

          <footer className={cn("flex w-full shrink-0 flex-col gap-3 p-6", railCollapsed && "hidden")}>
            {!test?.isFinished && (
              <button
                onClick={toggleShowSubmitModal}
                className="dp-button-primary h-12 self-stretch px-6"
              >
                <div className="whitespace-nowrap text-center text-base font-semibold">
                  {t("test.submit_exam")}
                </div>
              </button>
            )}
            <button
              onClick={handleShowExitModal}
              className="dp-button-secondary h-12 self-stretch px-6"
              data-testid="exit-exam"
            >
              <div className="whitespace-nowrap text-center text-base font-semibold">
                {t("test.exit_exam")}
              </div>
            </button>
          </footer>
        </aside>

        {/* Main question area */}
        <div className="relative flex h-full min-w-0 grow basis-0 items-start justify-center gap-6 overflow-auto bg-[#f4f4f6] px-6 pb-6 pt-[104px]">
          <header className="absolute inset-x-0 top-0 h-20 border-b border-[#dddfe4] bg-white px-3 py-4 sm:px-6">
            <div className="mx-auto flex h-full w-full max-w-[1200px] items-center gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-8 self-stretch">
                <div className="hidden w-[240px] shrink-0 items-center gap-3 xl:flex">
                  {testPresentation?.image ? (
                    <img alt="" className="h-10 w-10 shrink-0 object-contain" src={testPresentation.image} />
                  ) : null}
                  <div className="min-w-0">
                    <h1 className="truncate text-lg font-medium leading-[27px] text-[#0a0c11]">{testPresentation?.title || t(test?.testMode === TestMode.EXAM ? "study.exam_simulation_title" : "study.quick_practice_title")}</h1>
                    <p className="truncate text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">{examSubtitle}</p>
                  </div>
                </div>
                <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-[#efefef]">
                    <div className="h-full rounded-full bg-[var(--dp-brand-button)] transition-[width] duration-300" style={{ width: `${examProgress}%` }} />
                  </div>
                  <span className="whitespace-nowrap text-sm font-medium leading-6 text-[#0a0c11] sm:text-base">{t("test.question_counter", { current: displayQuestionNumber, total: totalDisplayQuestions })}</span>
                </div>
              </div>
              {test?.testMode === TestMode.EXAM && test.startTime && (
                <ExamCountdown startTimeMs={test.startTime.valueOf()} examTimeMinutes={examTimeMinutes} />
              )}
            </div>
          </header>
          {/* Passage Intro Screen - shown when on a passage parent */}
          {/* Remaining time — EXAM mode only; practice runs are untimed.
              Placed ABOVE the passage/question ternary deliberately: it previously lived
              inside the question branch, so the clock DISAPPEARED on passage-intro
              screens. That is not hypothetical — the IBEW Aptitude Exam Simulation is a
              TestMode.EXAM with numPassages: 2 (appsConfig), so the flagship timed exam
              had two screens with no visible timer, and they are exactly the screens a
              student lingers on.
              Self-contained so its per-second tick cannot re-render ExamScreen. */}
          {currentIsPassageParent && passageInfo ? (
            <div className="dp-card flex min-w-0 max-w-[1200px] grow flex-col items-start justify-start p-8">
              {/* Passage header */}
              <div className="self-stretch flex-col justify-start items-start gap-6 flex">
                <div className="flex w-full items-center justify-between gap-3">
                  <button
                    type="button"
                    aria-label={t("test.questions_sidebar_title")}
                    onClick={() => setQuestionsDrawerOpen(true)}
                    className="-ml-1 shrink-0 rounded-lg p-1 text-[#4b5563] hover:bg-zinc-100 lg:hidden"
                  >
                    <Menu className="w-6 h-6" />
                  </button>
                  <span className="flex items-center gap-2 text-xl font-bold text-[#0a0c11]">
                    <FileText aria-hidden="true" className="h-5 w-5" />
                    {t("test.passage_label", { number: currentPassageNumber })}
                  </span>
                  <span className="rounded-full bg-[#f4f4f6] px-3 py-1 text-sm font-medium text-[#5b616d]">
                    {t("test.passage_progress", {
                      current: currentPassageNumber,
                      total: passageGroups.groups.length,
                    })}
                  </span>
                </div>

                <h1 className="text-xl font-bold leading-7 text-[#0a0c11]">{passageInfo.passageTitle}</h1>

                {/* Full passage text */}
                <div className="max-h-[60vh] self-stretch overflow-auto rounded-2xl bg-[#f4f4f6] p-6">
                  <p
                    className="text-black text-base font-normal font-inter leading-7 whitespace-pre-wrap"
                    data-testid="passage-text"
                  >
                    {passageInfo.passageText}
                  </p>
                </div>

                <p className="text-sm text-[#a1a5ac]">
                  {t("test.passage_intro_helper")}
                </p>
                <div className="flex w-full items-center gap-3">
                  <button className="dp-button-secondary h-12 px-6 disabled:opacity-40" disabled={previousDisabled} onClick={previousQuestion} type="button">
                    {t("common.previous")}
                  </button>
                  <button className="dp-button-primary h-12 px-6 disabled:opacity-40" disabled={nextDisabled} onClick={nextQuestion} type="button">
                    {t("common.next")}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Question and answers section - shown for regular questions and passage children */
            <div className="dp-card flex min-h-[608px] min-w-0 max-w-[856px] grow flex-col items-start gap-6 p-8">
              {/* Question text */}
              <div className="flex self-stretch flex-col items-start justify-start gap-6">
                <div className="flex-col justify-start items-start flex w-full">
                  <div className="flex self-stretch flex-col items-start gap-3 lg:flex-row lg:justify-between">
                    <div className="flex items-start gap-2 min-w-0">
                      <button
                        type="button"
                        aria-label={t("test.questions_sidebar_title")}
                        onClick={() => setQuestionsDrawerOpen(true)}
                        className="-ml-1 shrink-0 rounded-lg p-1 text-[#4b5563] hover:bg-zinc-100 lg:hidden"
                      >
                        <Menu className="w-6 h-6" />
                      </button>
                      <div className="min-w-0">
                        <span className="sr-only">{t("test.question_counter", { current: displayQuestionNumber, total: totalDisplayQuestions })}</span>
                        <span className="block text-xl font-semibold leading-[26px] text-[#0a0c11]" data-testid="question-text">
                          {question?.question}
                        </span>
                      </div>
                    </div>
                    <div className="pl-8 xl:hidden">
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="flex flex-col items-center whitespace-nowrap">
                            <Lightbulb
                              className={cn(
                                "w-[26px] h-[26px] text-black",
                                !hintContent && "opacity-30"
                              )}
                            />
                            <span className="text-xs text-center font-medium whitespace-nowrap">
                              {isQuestionSubmitted || test?.isFinished ? t("test.explanation") : t("test.hint")}
                            </span>
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                          <DialogTitle>{isQuestionSubmitted || test?.isFinished ? t("test.explanation") : t("test.hint")}</DialogTitle>
                          <div className="pt-2 [overflow-wrap:anywhere]">
                            {hintContent ? (
                              <ParsedHintText text={hintContent} />
                            ) : (
                              <p className="text-[#9a9a9a] text-sm font-normal font-inter">
                                {t("test.no_hint_available")}
                              </p>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>

                    </div>
                  </div>
                </div>

                {diagramUrl && (
                  <ExamExhibit
                    alt={t("test.diagram_alt")}
                    caption={String(
                      question?.diagram_caption ||
                        t("test.exhibit_fallback_caption"),
                    )}
                    expandLabel={t("test.expand_exhibit")}
                    imageUrl={diagramUrl}
                    title={t("test.diagram_title")}
                  />
                )}

                {/* Answer options */}
                <div className="flex self-stretch flex-col items-center justify-start gap-3 overflow-hidden">
                  {renderAnswers()}
                </div>
                <p className="sr-only">{t("test.copyright_notice", { year: new Date().getFullYear() })}</p>
              </div>

              <div className="flex w-full flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center sm:gap-6">
                <div className="flex items-center gap-3">
                  <button className="dp-button-secondary h-12 flex-1 px-4 disabled:opacity-40 sm:flex-none sm:px-6" disabled={previousDisabled} onClick={previousQuestion} type="button">
                    {t("common.previous")}
                  </button>
                  <button
                    className={cn(
                      nextButtonIsPrimary
                        ? "dp-button-primary"
                        : "dp-button-secondary",
                      "h-12 flex-1 px-4 disabled:opacity-40 sm:flex-none sm:px-6",
                    )}
                    disabled={nextDisabled}
                    onClick={nextQuestion}
                    type="button"
                  >
                    {t("common.next")}
                  </button>
                </div>
                {isLastQuestion ? (
                  <button
                    className="dp-button-primary h-12 w-full px-6 disabled:bg-[var(--dp-brand-button)] disabled:opacity-40 sm:w-auto sm:min-w-[142px]"
                    disabled={!selectedAnswer}
                    onClick={toggleShowSubmitModal}
                    type="button"
                  >
                    {t("test.view_results")}
                  </button>
                ) : canSeeSubmit ? (
                  <button
                    className="dp-button-primary h-12 w-full px-6 disabled:bg-[var(--dp-brand-button)] disabled:opacity-40 sm:w-auto sm:min-w-[166px]"
                    disabled={!selectedAnswer}
                    onClick={submitAnswer}
                    type="button"
                  >
                    {t("test.submit_answer")}
                  </button>
                ) : null}
              </div>
              <div className="grid w-full grid-cols-1 items-center gap-4 border-t border-[#dddfe4] pt-6 text-sm text-[#5b616d] sm:grid-cols-[1fr_auto_1fr]">
                <div className="flex flex-wrap items-center gap-6 sm:justify-self-start">
                  <button
                    aria-pressed={!!test?.starredQuestions[questionIndex]}
                    aria-label={t("test.star_question_aria", {
                      number: displayQuestionNumber,
                      defaultValue: "Flag question {{number}}",
                    })}
                    className="flex items-center gap-2 font-medium tracking-[0.07px]"
                    data-starred={!!test?.starredQuestions[questionIndex]}
                    data-testid="star-question"
                    onClick={() => handleStarQuestion(questionIndex, questionId || "", !!test?.starredQuestions[questionIndex])}
                    type="button"
                  >
                    <Flag aria-hidden="true" className={cn("h-6 w-6", test?.starredQuestions[questionIndex] && "fill-current text-[#f7a619]")} />
                    {t("test.flag_question")}
                  </button>
                </div>
                {currentIsPassageChild && passageInfo ? (
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="flex items-center justify-center gap-2 font-semibold text-[var(--dp-brand-deep)] sm:justify-self-center" type="button">
                        <FileText aria-hidden="true" className="h-5 w-5" />
                        {t("test.view_passage")}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="left-auto right-0 top-0 h-[100dvh] max-h-[100dvh] w-[min(560px,100vw)] max-w-none translate-x-0 translate-y-0 grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden rounded-none border-0 p-0">
                      <DialogTitle className="flex h-[72px] shrink-0 items-center gap-2 border-b border-[#dddfe4] px-6 pr-14 text-xl leading-7 sm:px-8 sm:pr-14">
                        <FileText aria-hidden="true" className="h-5 w-5" />
                        {t("test.passage_label", { number: currentPassageNumber })}
                      </DialogTitle>
                      <div className="min-h-0 overflow-y-auto px-6 py-6 sm:px-8">
                        <h2 className="text-xl font-bold leading-7 text-[#0a0c11]">{passageInfo.passageTitle}</h2>
                        <p className="mt-6 whitespace-pre-wrap text-base leading-7 text-[#0a0c11]">{passageInfo.passageText}</p>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : <span />}
                <button className="flex items-center gap-2 font-medium tracking-[0.07px] sm:justify-self-end" data-testid="report-question" onClick={toggleShowReportIncorrectQn} type="button">
                  <ShieldAlert aria-hidden="true" className="h-6 w-6" />
                  {t("test.report_issue")}
                </button>
              </div>
            </div>
          )}
          {/* Right sidebar with passage, hint and diagram - hidden for passage parents */}
          {!currentIsPassageParent && (
          <div className="hidden h-[608px] w-[320px] shrink-0 flex-col items-start justify-start gap-6 xl:flex">
            <div className="dp-card flex min-h-[232px] w-full grow basis-0 flex-col items-start gap-6 overflow-hidden p-6">
              <div className="flex w-full items-center gap-3">
                <Lightbulb aria-hidden="true" className="h-6 w-6 text-[var(--dp-brand)]" />
                <h2 className="text-lg font-semibold leading-[27px] text-[#0a0c11]">
                  {t("test.explanation")}
                </h2>
              </div>
              <div className="h-px w-full shrink-0 bg-[#dddfe4]" />
              {hintContent ? (
                <div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-y-auto text-left text-sm leading-[21px] text-[#5b616d]">
                  {showHint || isQuestionSubmitted || test?.isFinished ? (
                    <div className="h-full w-full overflow-y-auto [overflow-wrap:anywhere]">
                      <ParsedHintText text={hintContent} />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-6 text-center">
                      <div className="flex w-[156px] flex-col items-center gap-1">
                        <strong className="text-lg font-medium leading-[27px] text-[#0a0c11]">{t("test.need_hint")}</strong>
                        <span>{t("test.hint_guidance")}</span>
                      </div>
                      <button className="dp-button-secondary h-12 px-6 text-base" onClick={() => toggleShowHint()} type="button">
                        {t("test.show_hint")}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex size-full items-center justify-center">
                  <p className="text-[#9a9a9a] text-sm font-normal font-inter">
                    {t("test.no_hint_available")}
                  </p>
                </div>
              )}
            </div>

            {/* Diagram section */}
            {diagramUrl && (
              <div className="hidden">
                <div className="self-stretch h-12 justify-between items-start inline-flex">
                  <div className="text-black text-base font-medium font-inter">
                    {t("test.diagram_title")}
                  </div>
                  <Dialog>
                    <DialogTrigger>
                      <Maximize2 className="w-6 h-6 text-black" />
                    </DialogTrigger>
                    <DialogContent className="max-w-full w-[900px]">
                      <DialogTitle>{t("test.diagram_title")}</DialogTitle>
                      <img
                        className="grow shrink basis-0 self-stretch h-full"
                        src={diagramUrl}
                        alt={t("test.diagram_alt")}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="justify-center items-center gap-2.5 inline-flex">
                  <img
                    className="grow shrink basis-0 self-stretch h-full"
                    src={diagramUrl}
                    alt={t("test.diagram_alt")}
                  />
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
      <QuestionReportModal
        open={showReportIncorrectQn}
        onOpenChange={toggleShowReportIncorrectQn}
        question={question}
        selectedAnswer={selectedAnswer}
        email={userEmail}
      />

      <SubmitExamModal
        open={showSubmitModal}
        onOpenChange={toggleShowSubmitModal}
        onCancel={toggleShowSubmitModal}
        onContinue={handleSubmitExam}
      />

      <ExitExamModal
        open={showExitModal}
        onOpenChange={toggleShowExitModal}
        onCancel={toggleShowExitModal}
        onContinue={handleExitExam}
      />
    </div>
  );
});

export default ExamScreen;
